import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

// Dialog shown when a shift save returns 409 with code: 'auth_overage'.
// Forces the scheduler to enter a reason before retrying the save with
// override_reason in the payload (which the backend logs + notifies on).

export default function OverrideDialog({ state, onConfirm, onCancel, busy }) {
  const [reason, setReason] = useState('');
  if (!state) return null;
  const breaches = state.breaches || [];
  const canSubmit = reason.trim().length >= 5;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel} data-testid="override-dialog">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-[#FDECEA] bg-[#FDECEA]/40 flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-[#D9534F] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-base font-bold text-[#2D1B4E]">Authorization Limit Exceeded</h3>
            <p className="text-xs text-[#5A4D6E] mt-0.5">
              Saving this shift will put one or more participants over their authorized weekly amount.
              Logging an override creates a compliance audit entry and notifies supervisors.
            </p>
          </div>
          <button onClick={onCancel} className="text-[#8E85A0] hover:text-[#2D1B4E]"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-3">
          {/* Breaches */}
          <div className="space-y-2">
            {breaches.map((b, i) => {
              const isPeriod = b.scope === 'period';
              return (
                <div key={i} className={`rounded-md border p-3 text-sm ${isPeriod ? 'border-[#9B5DE5]/40 bg-[#EDE7F6]/40' : 'border-[#D9534F]/30 bg-[#FDECEA]/40'}`} data-testid={`breach-${i}`}>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#2D1B4E]">{b.participant_name}</p>
                    <span className="text-[#8E85A0] font-normal">·</span>
                    <span className="text-[#8E85A0] font-normal">{b.service_label}</span>
                    <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isPeriod ? 'bg-[#EDE7F6] text-[#6B3FA0] border border-[#9B5DE5]/40' : 'bg-[#FDECEA] text-[#D9534F] border border-[#D9534F]/40'}`}>
                      {isPeriod ? 'Period Cap' : 'Weekly Cap'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1.5 text-xs text-[#5A4D6E] font-mono">
                    <span>Currently used: <strong>{b.current_hours}h</strong> ({(b.current_hours * 4).toFixed(0)}u)</span>
                    <span>This shift: <strong>+{b.new_shift_hours}h</strong></span>
                    <span>Authorized: <strong>{b.auth_hours}h</strong> ({b.auth_units}u)</span>
                    <span className="text-[#D9534F]">Overage: <strong>+{b.overage_hours}h</strong> ({(b.overage_hours * 4).toFixed(1)}u)</span>
                  </div>
                  {isPeriod && (b.already_utilized_hours || b.period_scheduled_hours) && (
                    <div className="grid grid-cols-2 gap-2 mt-1 text-[10px] text-[#8E85A0] font-mono">
                      <span>Already utilized (external): {b.already_utilized_hours || 0}h</span>
                      <span>Already scheduled in period: {b.period_scheduled_hours || 0}h</span>
                    </div>
                  )}
                  <p className="text-[10px] text-[#8E85A0] mt-1">
                    {isPeriod
                      ? <>Period {b.period_start} → {b.period_end}</>
                      : <>Week {b.week_start} → {b.week_end}</>}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Reason */}
          <div className="space-y-1.5 pt-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2D1B4E]">
              Emergency override reason <span className="text-[#D9534F]">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Family emergency — alternate caregiver unavailable, parent requested coverage extension to Saturday."
              className="w-full border border-[#DED8EB] rounded px-3 py-2 text-sm min-h-[90px] focus:outline-none focus:ring-2 focus:ring-[#9B7ED8]"
              data-testid="override-reason-input"
              autoFocus
            />
            <p className="text-[10px] text-[#8E85A0]">
              Min 5 characters. This reason is permanently logged and visible to all admins for audit.
            </p>
          </div>
        </div>

        <div className="p-4 bg-[#F5F1FA] border-t border-[#DED8EB] flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy}
            className="px-4 py-1.5 rounded text-sm font-medium text-[#5A4D6E] hover:bg-white border border-[#DED8EB]"
            data-testid="override-cancel-btn">
            Cancel — don't save
          </button>
          <button onClick={() => onConfirm(reason.trim())} disabled={!canSubmit || busy}
            className="px-4 py-1.5 rounded text-sm font-bold text-white bg-[#D9534F] hover:bg-[#C9302C] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="override-confirm-btn">
            {busy ? 'Saving…' : 'Override & save'}
          </button>
        </div>
      </div>
    </div>
  );
}
